/**
 * WS-NAV — chrome row primitives shared by the desktop sidebar, the workspace
 * switcher flyout and the mobile drawer.
 *
 * Extracted verbatim from the story-050 `AppSidebar.tsx` (row geometry, the
 * `sidebar.*` token wiring, the collapsed-rail tooltip) so the three surfaces
 * cannot drift apart. The values themselves are unchanged — this is a move,
 * not a restyle.
 */
import type { ComponentType, ReactElement, ReactNode } from "react";
import type { Theme } from "@mui/material/styles";
import { Box, Tooltip } from "@mui/material";
import {
  chrome,
  sidebar as sidebarTokens,
  workspaceAccent,
} from "@aeriosiq/ui";

// D86 — the geometry now lives in `@aeriosiq/ui`'s `chrome` tokens, because the
// Platform Admin console renders the same rail (D90) and was carrying its own
// copy of these numbers. The names are kept as re-exports rather than replaced
// at ~20 call sites: this is a tokenisation, not a rename, and mixing the two
// would bury it.
//
// `expandedWidth` is derived there as rail + panel, so the 64 + 208 = 272
// invariant that keeps pages from reflowing cannot be broken by editing one
// number.
export const EXPANDED_WIDTH = chrome.expandedWidth;
export const COLLAPSED_WIDTH = chrome.railWidth;
export const PANEL_WIDTH = chrome.panelWidth;
export const RAIL_HEADER_H = chrome.headerHeight;
export const ROW_HEIGHT = chrome.rowHeight;
export const LEAF_HEIGHT = chrome.leafHeight;
export const TOUCH_ROW_HEIGHT = chrome.touchRowHeight;

export type IconComponent = ComponentType<{
  fontSize?: "small" | "inherit" | "medium" | "large";
  sx?: object;
}>;

/**
 * Tooltip variant — visible only when the sidebar is collapsed. MUI's
 * Tooltip is unconditionally rendered as a portal; we just gate the
 * `title` prop so the tooltip mounts as empty (and so never appears)
 * when the sidebar is expanded.
 */
export function CollapsedTooltip({
  collapsed,
  title,
  children,
}: {
  collapsed: boolean;
  title: string;
  children: ReactNode;
}): ReactElement {
  if (!collapsed) {
    return <>{children}</>;
  }
  return (
    <Tooltip title={title} placement="right" disableInteractive arrow={false}>
      <Box component="span" sx={{ display: "flex", width: "100%" }}>
        {children}
      </Box>
    </Tooltip>
  );
}

export function rowStyles(opts: {
  selected: boolean;
  collapsed: boolean;
  height?: number;
  pl?: number;
  ml?: number;
  mr?: number;
  /** WS-NAV — the Platform workspace paints its selected pill indigo. */
  accent?: "platform" | undefined;
}): (theme: Theme) => Record<string, unknown> {
  const { selected, collapsed, height = ROW_HEIGHT, pl, ml, mr, accent } = opts;
  return (theme: Theme): Record<string, unknown> => {
    const mode = theme.palette.mode;
    const tokens = mode === "dark" ? sidebarTokens.dark : sidebarTokens.light;
    const accentTokens =
      mode === "dark" ? workspaceAccent.dark : workspaceAccent.light;
    const hoverBg = tokens.rowHoverBg;
    const pressedBg = tokens.rowPressedBg;
    const selectedBg =
      accent === "platform" ? accentTokens.solid : tokens.selectedBg;
    const selectedFg =
      accent === "platform" ? accentTokens.solidFg : tokens.selectedFg;
    const horizontalInset = (ml ?? 0) + (mr ?? 0);
    return {
      width:
        horizontalInset > 0 && !collapsed
          ? `calc(100% - ${String(horizontalInset * 8)}px)`
          : "100%",
      minHeight: height,
      height,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      pl: collapsed ? 0 : (pl ?? 2),
      pr: collapsed ? 0 : 1.5,
      ml: collapsed ? 0 : (ml ?? 0),
      mr: collapsed ? 0 : (mr ?? 0),
      justifyContent: collapsed ? "center" : "flex-start",
      borderRadius: 1.5,
      color: selected ? selectedFg : "text.primary",
      textDecoration: "none",
      bgcolor: selected ? selectedBg : "transparent",
      transition: theme.transitions.create("background-color", {
        duration: 120,
        easing: "cubic-bezier(0.2, 0, 0, 1)",
      }),
      "&:hover": {
        bgcolor: selected ? selectedBg : hoverBg,
      },
      "&:active": {
        bgcolor: selected ? selectedBg : pressedBg,
      },
      "&.Mui-disabled, &[aria-disabled='true']": {
        color: "text.disabled",
        cursor: "not-allowed",
        pointerEvents: "auto",
        "&:hover": { bgcolor: "transparent" },
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: "2px",
      },
    };
  };
}

export function IconCell({
  Icon,
  selected,
  disabled,
  accent,
}: {
  Icon: IconComponent;
  selected: boolean;
  disabled?: boolean;
  accent?: "platform" | undefined;
}): ReactElement {
  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        // When selected, inherit the white `selectedFg` from the parent
        // row so the icon reads against the accent pill. Otherwise dim the
        // icon down so unselected rows recede — except the Platform accent,
        // which stays indigo so the cross-tenant workspace is identifiable
        // even in the collapsed icon rail.
        color: disabled
          ? "text.disabled"
          : selected
            ? "inherit"
            : accent === "platform"
              ? (t) =>
                  t.palette.mode === "dark"
                    ? workspaceAccent.dark.fg
                    : workspaceAccent.light.fg
              : "text.secondary",
      }}
    >
      <Icon fontSize="small" />
    </Box>
  );
}

/**
 * True when `pathname` should mark the nav item pointing at `to` as current.
 * Exact match, plus deeper segments so a detail route keeps its list item
 * highlighted (`/projects/<id>` → "Projects"). `/` is exact-only — every path
 * starts with it.
 */
export function isNavItemCurrent(
  pathname: string,
  to: string,
  siblings: readonly (string | undefined)[] = [],
): boolean {
  if (to === "/") return pathname === "/";
  const matches = (t: string) => pathname === t || pathname.startsWith(`${t}/`);
  if (!matches(to)) return false;
  // DI-3-D — a sibling leaf deeper under this one wins its own subtree:
  // `/field-data/intake/history` marks "Intake history", not "My intake".
  return !siblings.some(
    (s) => s !== undefined && s !== to && s.startsWith(`${to}/`) && matches(s),
  );
}
